// chat-history-and-id.resolver.ts
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Logger } from '@iese-chatbot/common-utils';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, takeUntil, tap } from 'rxjs';
import { ChatDataActions } from '../state/actions/chat-data.actions';
import { selectChatEntities, selectFetchDone } from '../state/selectors/chat-data.selectors';

export const chatHistoryAndIdResolver: ResolveFn<boolean> = (route) => {
  const router = inject(Router);
  const store = inject(Store);
  const chatId = route.paramMap.get('chatId');
  const destroy$ = new BehaviorSubject<boolean>(false);

  if (chatId) {
    combineLatest([store.select(selectFetchDone), store.select(selectChatEntities)])
      .pipe(
        takeUntil(destroy$),
        filter(([fetchDone, chatHistory]) => fetchDone === true && Object.keys(chatHistory ?? {}).length > 0),
        map(([_, chatHistory]) => chatHistory),
        tap((chatHistory) => {
          if (!chatHistory[chatId]) {
            Logger.error(`Chat '${chatId}' not found. Redirecting to chat list.`);
            router.navigate(['', 'chat']).then();
          } else {
            Logger.ok(`Chat '${chatId}' has been correctly loaded.`);
          }
          destroy$.next(true);
          destroy$.complete();
        }),
      )
      .subscribe();
  }

  return store.select(selectFetchDone).pipe(
    map((fetchDone) => {
      if (!fetchDone) {
        Logger.info('Chat history not loaded yet. Fetching data now.');
        store.dispatch(ChatDataActions.fetchChatHistory());
      } else {
        Logger.info('Chat history already loaded. Skipping fetch.');
      }
      return fetchDone;
    }),
  );
};
